<template>
  <div class="ad-insights-view">
    <v-tabs v-model="mainTab" background-color="transparent" class="ad-insights-view__tabs mb-3" height="40">
      <v-tab>
        <v-icon small class="mr-1">mdi-finance</v-icon>
        Anzeigen
      </v-tab>
      <v-tab>
        <v-icon small class="mr-1">mdi-email-outline</v-icon>
        E-Mail-Marketing
      </v-tab>
    </v-tabs>

    <EmailMarketingPanel v-if="mainTab === 1" />

    <div v-show="mainTab === 0">
    <v-card light flat class="ad-insights-view__card ad-insights-view__toolbar">
      <StatisticsHeader hide-comparison>
        <template #prepend>
          <v-select
            v-if="shopItems.length"
            v-model="selectedShopCode"
            :items="shopItems"
            label="Shop"
            class="ad-insights-view__shop-select"
            dense
            outlined
            hide-details
          />
        </template>
      </StatisticsHeader>
      <div class="ad-insights-view__toolbar-row" v-if="breakEvenRoas">
        <v-chip small outlined class="ad-insights-view__breakeven">
          Break-even-ROAS ≈ {{ number(breakEvenRoas) }}
        </v-chip>
      </div>
    </v-card>

    <StateDisplay
      class="mt-12"
      v-if="statisticLoading || statisticError"
      :loading="statisticLoading"
      :error="statisticError"
    />

    <v-row v-else-if="statistic && statistic.shops.length" class="mt-1">
      <v-col cols="12" :lg="chatOpen ? 7 : 12">
        <v-card light flat class="ad-insights-view__card pa-4">
          <div class="ad-insights-view__card-title">
            <v-icon small color="primary" class="mr-2">mdi-finance</v-icon>
            Anzeigen-Performance – {{ selectedShopDomain }}
            <v-spacer />
            <v-btn small depressed color="primary" @click="showPublishDialog = true">
              <v-icon small left>mdi-plus</v-icon>
              Neue Anzeige
            </v-btn>
            <v-btn v-if="!chatOpen" small text color="primary" class="ml-2" @click="chatOpen = true">
              <v-icon small left>mdi-creation</v-icon>
              Chat öffnen
            </v-btn>
          </div>
          <div v-if="!adsConfirmed" class="ad-insights-view__confirm">
            <v-icon size="34" color="primary" class="mb-2">mdi-finance</v-icon>
            <div class="ad-insights-view__confirm-text">
              Anzeigen-Daten werden erst auf Bestätigung geladen (externe Werbe-APIs).
            </div>
            <v-btn color="primary" depressed class="mt-3" @click="adsConfirmed = true">
              <v-icon small left>mdi-download</v-icon>
              Anzeigen-Daten laden
            </v-btn>
          </div>
          <AdInsightsPanel
            v-else
            :active="!!selectedShopCode"
            :shop-code="selectedShopCode"
            :break-even-roas="breakEvenRoas"
            :shop-revenue="shopRevenue"
            :shop-profit="shopProfit"
            @loaded="insights = $event"
          />
        </v-card>
      </v-col>
      <!-- v-show keeps the chat history alive while collapsed -->
      <v-col v-show="chatOpen" cols="12" lg="5">
        <AdInsightsChat
          collapsible
          :shop-code="selectedShopCode"
          :insights="insights"
          :break-even-roas="breakEvenRoas"
          :shop-revenue="shopRevenue"
          :shop-profit="shopProfit"
          @collapse="chatOpen = false"
        />
      </v-col>
    </v-row>

    <AdPublishDialog
      v-model="showPublishDialog"
      :shop-code="selectedShopCode"
      :domain="selectedShopDomain"
      :insights="insights"
      :break-even-roas="breakEvenRoas"
    />
    </div>
  </div>
</template>

<script>
import StatisticsHeader from "@/components/Statistics/StatisticsHeader";
import StateDisplay from "@/components/StateDisplay";
import AdInsightsPanel from "@/components/Statistics/StatisticByShops/AdInsightsPanel";
import AdInsightsChat from "@/components/Statistics/StatisticByShops/AdInsightsChat";
import AdPublishDialog from "@/components/Statistics/StatisticByShops/AdPublishDialog";
import EmailMarketingPanel from "@/components/EmailMarketing/EmailMarketingPanel";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { getDecimalNumberString } from "@/utils";
import dayjs from "dayjs";

const timezone = require("dayjs/plugin/timezone");
const utc = require("dayjs/plugin/utc");

dayjs.extend(utc);
dayjs.extend(timezone);

// Restore the last shop + period on the next visit.
const STORAGE_SHOP_KEY = "adInsights.shopCode";
const STORAGE_RANGE_KEY = "adInsights.range";

const readStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // Storage unavailable (e.g. private mode) — persistence is best-effort.
  }
};

export default {
  name: "AdInsightsView",
  components: { AdPublishDialog, AdInsightsChat, AdInsightsPanel, StateDisplay, StatisticsHeader, EmailMarketingPanel },
  data() {
    return {
      mainTab: 0,
      adsConfirmed: false,
      selectedShopCode: null,
      insights: null,
      showPublishDialog: false,
      chatOpen: true,
    };
  },
  computed: {
    ...mapGetters(["statistic", "statisticLoading", "statisticError", "statisticRange"]),
    shopItems() {
      return (this.statistic?.shops || []).map((shop) => ({
        text: shop.domain,
        value: shop.shopCode,
      }));
    },
    selectedShop() {
      return (this.statistic?.shops || []).find(
        (shop) => shop.shopCode === this.selectedShopCode
      ) || null;
    },
    selectedShopDomain() {
      return this.selectedShop?.domain || "";
    },
    shopRevenue() {
      return this.selectedShop?.revenue?.total ?? null;
    },
    shopProfit() {
      if (!this.selectedShop) return null;
      const totalCosts = Object.keys(this.selectedShop.costs || {}).reduce(
        (sum, key) => sum + (this.selectedShop.costs[key] || 0),
        0
      );
      return Number(((this.selectedShop.revenue?.total || 0) - totalCosts).toFixed(2));
    },
    breakEvenRoas() {
      if (!this.selectedShop) return null;
      const revenue = this.selectedShop.revenue?.total || 0;
      const nonAdCosts = Object.keys(this.selectedShop.costs || {})
        .filter((key) => key !== "advertisement")
        .reduce((sum, key) => sum + (this.selectedShop.costs[key] || 0), 0);
      const margin = revenue - nonAdCosts;
      return revenue > 0 && margin > 0 ? revenue / margin : null;
    },
  },
  watch: {
    shopItems: {
      immediate: true,
      handler(items) {
        if (!items.length) return;
        if (!this.selectedShopCode || !items.some((item) => item.value === this.selectedShopCode)) {
          const storedShop = readStorage(STORAGE_SHOP_KEY);
          this.selectedShopCode = items.some((item) => item.value === storedShop)
            ? storedShop
            : items[0].value;
        }
      },
    },
    selectedShopCode(code) {
      if (code) writeStorage(STORAGE_SHOP_KEY, code);
    },
    statisticRange: {
      deep: true,
      handler(range) {
        if (range?.min && range?.max) writeStorage(STORAGE_RANGE_KEY, JSON.stringify(range));
      },
    },
  },
  mounted() {
    if (!this.statistic) {
      this.setStatisticRange(this.storedRange() || this.todayRange());
      this.loadStatistic();
    }
  },
  methods: {
    ...mapActions(["loadStatistic"]),
    ...mapMutations(["setStatisticRange"]),
    todayRange() {
      const currentLocalizedDate = dayjs().tz("Europe/Berlin", true);
      return {
        min: currentLocalizedDate.startOf("day").format(),
        max: currentLocalizedDate.endOf("day").format(),
      };
    },
    storedRange() {
      try {
        const range = JSON.parse(readStorage(STORAGE_RANGE_KEY));
        return range?.min && range?.max ? range : null;
      } catch (e) {
        return null;
      }
    },
    number(value, fraction = 2) {
      return getDecimalNumberString(value, fraction);
    },
  },
};
</script>

<style lang="scss" scoped>
.ad-insights-view {
  width: 100%;
}

.ad-insights-view__card {
  border-radius: 14px !important;
  box-shadow: rgba(14, 63, 126, 0.05) 0 0 0 1px, rgba(42, 51, 70, 0.05) 0 4px 12px -2px !important;
}

.ad-insights-view__toolbar {
  padding: 16px 16px 12px;
}

.ad-insights-view__toolbar-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 4px;
}

.ad-insights-view__shop-select {
  width: 230px;
  max-width: 280px;
  flex: 0 0 auto;
}

.ad-insights-view__breakeven {
  font-weight: 600;
}

.ad-insights-view__card-title {
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 4px;
}
.ad-insights-view__confirm {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 56px 20px;
  text-align: center;
}
.ad-insights-view__confirm-text {
  font-size: 13.5px;
  color: rgba(0, 0, 0, 0.55);
}
</style>
